import { useCallback, useEffect, useState } from "react";
import ReferenceDataNote from "@/components/backtest/ReferenceDataNote";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import algoExecutionService from "@/services/algoExecutionService";
import rsiMomentumService, { LIVE_CONFIRMATION_PHRASE } from "@/services/rsiMomentumService";
import RsiMomentumRulesCard from "./RsiMomentumRulesCard";
import BacktestTradeVerification from "./rsi-backtest-verification/BacktestTradeVerification";
import { istText } from "@/utils/istTime";
import rsiBacktestVerificationService from "@/services/rsiBacktestVerificationService";
import { rulesSummary } from "./rsiMomentumRules";
import type {
  RsiBacktestRunSummary,
  RsiMomentumBacktestResult,
  RsiMomentumDashboard,
  RsiMomentumLimits,
  RsiMomentumOrderEvent,
  RsiMomentumPosition,
  RsiMomentumRules,
  RsiMomentumState,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

/**
 * Administration > RSI Momentum -- operating dashboard for the NIFTY
 * intraday TEK_RSI_MOMENTUM strategy: paper/live status, indicator context,
 * current position with NIFTY-level stop/target, risk usage, signal and
 * trade history, alerts, backtest runner, and the strategy's controls
 * (enable, pause entries, kill switch, live approval).
 */

const REFRESH_MS = 15000;

const STATE_LABEL: Record<RsiMomentumState, { label: string; color: "default" | "info" | "success" | "warning" | "error" | "primary" }> = {
  PAPER_TRADING: { label: "Paper trading", color: "info" },
  LIVE_TRADING: { label: "Live trading", color: "error" },
  NO_SIGNAL: { label: "No signal", color: "default" },
  SIGNAL_WAITING_FOR_ENTRY: { label: "Signal waiting for entry", color: "warning" },
  OPEN_POSITION: { label: "Open position", color: "primary" },
  TARGET_HIT: { label: "Target hit", color: "success" },
  STOP_LOSS_HIT: { label: "Stop-loss hit", color: "error" },
  EOD_SQUARE_OFF: { label: "EOD square-off", color: "default" },
  REJECTED_ORDER: { label: "Rejected order", color: "error" },
  DATA_UNAVAILABLE: { label: "Data unavailable", color: "warning" },
  DISABLED: { label: "Strategy disabled", color: "default" },
  NOT_EVALUATING: { label: "Not evaluating", color: "error" },
};

const EXIT_COLOR: Record<string, "success" | "error" | "default" | "warning"> = {
  TARGET_HIT: "success",
  STOP_LOSS_HIT: "error",
  EOD_SQUARE_OFF: "default",
  FORCED_EXIT_BACKSTOP: "warning",
  OPPOSITE_SIGNAL: "warning",
};

const fmt = (v: number | null | undefined, digits = 2) =>
  v === null || v === undefined || Number.isNaN(v) ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: digits, minimumFractionDigits: digits });

const money = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `₹${fmt(v)}`);

/** Every timestamp as "YYYY-MM-DD HH:MM:SS IST" (UTC-stored values converted). */
const time = (iso: string | null | undefined) => istText(iso);

function Metric({ label, value, tone }: { label: string; value: string; tone?: "success" | "error" }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight={600} color={tone ? `${tone}.main` : undefined}>
        {value}
      </Typography>
    </Box>
  );
}

const VWAP_LABEL: Record<string, string> = {
  FUTURES_VWAP: "VWAP (NIFTY futures, basis-adjusted)",
  OPTION_CHAIN_VOLUME_VWAP_PROXY: "VWAP proxy (option-chain volume)",
};

function vwapLabel(source: string | null | undefined): string {
  return source ? VWAP_LABEL[source] ?? source : "VWAP unavailable";
}

const ORDER_STATE_COLOR: Record<string, "default" | "info" | "success" | "warning" | "error"> = {
  CREATED: "default",
  SUBMITTED: "info",
  ACKNOWLEDGED: "info",
  PARTIALLY_FILLED: "warning",
  FILLED: "success",
  REJECTED: "error",
  CANCELLED: "warning",
  UNKNOWN: "error",
  TIMEOUT: "warning",
};

function OrdersTable({ rows }: { rows: RsiMomentumOrderEvent[] }) {
  if (!rows.length) return <Typography variant="body2" color="text.secondary">No orders yet.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Time (IST)</TableCell>
          <TableCell>Intent</TableCell>
          <TableCell>State</TableCell>
          <TableCell>Contract</TableCell>
          <TableCell align="right">Qty (filled)</TableCell>
          <TableCell align="right">Intended / limit / fill</TableCell>
          <TableCell align="right">Quote bid / ask (age, source)</TableCell>
          <TableCell align="right">Slippage ₹ (bps)</TableCell>
          <TableCell>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((o, i) => (
          <TableRow key={`${o.idempotency_key}-${o.state}-${i}`}>
            <TableCell>{time(o.time)}</TableCell>
            <TableCell>
              {o.intent} {o.side} #{o.attempt}
            </TableCell>
            <TableCell>
              <Chip size="small" label={o.state} color={ORDER_STATE_COLOR[o.state] ?? "default"} variant="outlined" />
            </TableCell>
            <TableCell>{o.contract ?? "—"}</TableCell>
            <TableCell align="right">
              {o.quantity} ({o.filled_quantity})
            </TableCell>
            <TableCell align="right">
              {fmt(o.intended_price)} / {fmt(o.limit_price)} / {fmt(o.avg_fill_price)}
            </TableCell>
            <TableCell align="right">
              {o.quote ? `${fmt(o.quote.bid)} / ${fmt(o.quote.ask)} (${fmt(o.quote.quote_age_seconds, 1)}s, ${o.quote.source})` : "—"}
            </TableCell>
            <TableCell align="right">
              {o.slippage?.rupees != null ? `${fmt(o.slippage.rupees)} (${fmt(o.slippage.bps, 1)})` : "—"}
            </TableCell>
            <TableCell>{o.reason ?? ""}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function trendContext(d: RsiMomentumDashboard): string {
  const ind = d.indicators;
  if (!ind || ind.close == null) return "No completed 5-minute bar evaluated yet.";
  const aboveVwap = ind.vwap != null && ind.close > ind.vwap;
  const aboveEma = ind.ema != null && ind.close > ind.ema;
  const momentumUp = ind.rsi_ema != null && ind.rsi_wma != null && ind.rsi_ema > ind.rsi_wma;
  if (ind.vwap == null) return "VWAP unavailable (no configured VWAP source has data) -- new entries are blocked.";
  if (aboveVwap && aboveEma) return `Bullish context: close above VWAP and EMA(20); RSI momentum ${momentumUp ? "rising" : "fading"}.`;
  if (!aboveVwap && !aboveEma) return `Bearish context: close below VWAP and EMA(20); RSI momentum ${momentumUp ? "rising" : "falling"}.`;
  return "Mixed context: VWAP and EMA(20) disagree -- no trend filter alignment.";
}

function exitTrigger(p: RsiMomentumPosition): string {
  const ex = (p.execution_details?.exit ?? null) as
    | { source?: string; checked_price?: number; tick_ts?: string; candle_start?: string }
    | null;
  if (!ex) return p.execution_details?.exit_blocked ? "EXIT BLOCKED (rejected)" : "—";
  const at = ex.tick_ts ? time(ex.tick_ts) : time(ex.candle_start);
  return `${ex.source ?? ""} @ ${fmt(ex.checked_price)} ${at}`;
}

function TradesTable({ rows }: { rows: RsiMomentumPosition[] }) {
  if (!rows.length) return <Typography variant="body2" color="text.secondary">No trades.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Entry (IST)</TableCell>
          <TableCell>Side</TableCell>
          <TableCell>Contract</TableCell>
          <TableCell align="right">Qty</TableCell>
          <TableCell align="right">NIFTY entry / SL / target</TableCell>
          <TableCell align="right">Option in → out</TableCell>
          <TableCell>Exit reason</TableCell>
          <TableCell align="right">P&amp;L</TableCell>
          <TableCell align="right">MFE / MAE (pts)</TableCell>
          <TableCell>Exit trigger</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((p) => (
          <TableRow key={p.id}>
            <TableCell>{time(p.entry_time)}</TableCell>
            <TableCell>
              <Chip size="small" label={p.side} color={p.side === "LONG" ? "success" : "error"} variant="outlined" />
            </TableCell>
            <TableCell>
              {fmt(p.strike, 0)} {p.option_type} {p.expiry_date}
            </TableCell>
            <TableCell align="right">{p.quantity}</TableCell>
            <TableCell align="right">
              {fmt(p.underlying_entry_price)} / {fmt(p.underlying_stop_loss)} / {fmt(p.underlying_target)}
            </TableCell>
            <TableCell align="right">
              {fmt(p.entry_price)} → {fmt(p.exit_price)}
            </TableCell>
            <TableCell>
              {p.exit_reason ? <Chip size="small" label={p.exit_reason} color={EXIT_COLOR[p.exit_reason] ?? "default"} /> : p.status}
            </TableCell>
            <TableCell align="right" sx={{ color: (p.realized_pnl ?? 0) >= 0 ? "success.main" : "error.main" }}>
              {money(p.realized_pnl)}
            </TableCell>
            <TableCell align="right">
              {fmt(p.mfe_points)} / {fmt(p.mae_points)}
            </TableCell>
            <TableCell>{exitTrigger(p)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Plain-language hints for the most common backtest rejection reasons. */
const REJECTION_HINTS: Record<string, string> = {
  MAX_LOTS_EXCEEDED:
    "Risk-based size is larger than “Max lots per trade” (trades are rejected, never scaled down). Lower “Risk per trade (%)” or raise “Max lots”.",
  QUANTITY_ZERO: "Risk budget is smaller than one lot at this stop distance. Raise “Risk per trade (%)” or capital.",
  RISK_AMOUNT_EXCEEDS_MAX: "Capital × risk % is above risk.max_risk_amount.",
  CALENDAR_UNKNOWN: "The trading calendar for that year is not loaded, so entries are blocked.",
  DATA_INSUFFICIENT_HISTORY: "Not enough warm-up bars yet (normal at the very start of the range).",
  DATA_INVALID: "The signal bar failed data checks (see “Invalid bars by reason”, e.g. a missed snapshot before it).",
  AFTER_ENTRY_CUTOFF: "Signal after the entry cut-off time (default 14:45) — expected.",
  FIRST_MINUTES_AFTER_OPEN: "Signal in the first minutes after 09:15 — expected.",
  BEFORE_ENTRY_START: "Signal before the entry start time (default 09:35) — expected.",
  ENTRY_AFTER_EOD: "The fill bar was at/after the square-off time — expected.",
  MAJOR_EVENT_BLACKOUT: "Inside an admin event blackout window.",
  MAX_TRADES_PER_DAY: "The day's trade cap (“Max trades per day”) was already used — later signals that day are skipped.",
  MAX_CONSECUTIVE_LOSSES: "Entries halted for the day after the “Max consecutive losses” streak.",
  MAX_DAILY_LOSS: "Realized loss reached “Max daily loss” — no new entries that day.",
  MAX_DAILY_LOSS_INCLUDING_UNREALIZED: "Realized + open loss reached “Max daily loss incl. unrealized” — no new entries that day.",
  MAX_DAILY_LOSS_PCT: "Daily loss reached risk.max_daily_loss_pct of capital — no new entries that day.",
  DAILY_PROFIT_TARGET: "Daily profit target reached — no new entries that day.",
  RISK_LIMITS_NOT_CONFIGURED: "Mandatory daily risk limits are missing — set them in “Limits & capital”.",
  STOP_NOT_BELOW_ENTRY: "Long signal skipped: the next bar opened below the signal bar's low, so the stop would be above the entry.",
  STOP_NOT_ABOVE_ENTRY: "Short signal skipped: the next bar opened above the signal bar's high, so the stop would be below the entry.",
};

/** Why a backtest produced the trades it did: data quality, signal funnel, rejections. */
function BacktestDiagnostics({ bt }: { bt: RsiMomentumBacktestResult }) {
  const raw = bt.data_quality ?? {};
  const dq: Record<string, number | undefined> = Object.fromEntries(
    Object.entries(raw).filter((entry): entry is [string, number] => typeof entry[1] === "number"),
  );
  const bySource = Object.entries((raw.spot_rows_by_source as Record<string, number> | undefined) ?? {});
  const invalidReasons = Object.entries((raw.invalid_bar_reasons as Record<string, number> | undefined) ?? {}).sort((a, b) => b[1] - a[1]);
  const diag = bt.diagnostics;
  const rejections = Object.entries(bt.rejections ?? {}).sort((a, b) => b[1] - a[1]);
  const missing = Object.entries(diag?.missing_indicator_counts ?? {}).sort((a, b) => b[1] - a[1]);
  const signals = diag ? Object.values(diag.raw_signals).reduce((a, b) => a + b, 0) : null;
  const trades = bt.metrics?.total_trades ?? 0;

  let summary: string | null = null;
  if (trades === 0) {
    if (!dq.bars) summary = "No 5-minute NIFTY bars were found in this range.";
    else if (diag && diag.bars_evaluated === 0)
      summary = `Indicators were never fully available (missing: ${missing.map(([k]) => k).join(", ") || "—"}), so no bar could be evaluated.`;
    else if (signals === 0) summary = "Data was fine, but the entry conditions were never all met in this range.";
    else if (rejections.length > 0)
      summary = `${signals ?? "Some"} signal(s) fired but every one was rejected — mostly ${rejections[0][0]}. ${REJECTION_HINTS[rejections[0][0]] ?? ""}`;
  }

  return (
    <Box sx={{ mt: 2 }} data-testid="backtest-diagnostics">
      {summary && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="backtest-why">
          {summary}
        </Alert>
      )}
      <Typography variant="subtitle2" gutterBottom>
        Data &amp; signal funnel
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {(
          [
            ["Sessions", dq.sessions],
            ["5-min bars", dq.bars],
            ["Invalid bars", dq.invalid_bars],
            ["Option-chain snapshots", dq.chain_snapshots],
            ["Bars without VWAP weight", dq.bars_without_vwap_weight],
            ["Lot size", dq.lot_size],
            ["Sessions from 1-min candles", dq.sessions_from_1min_candles],
            ["Sessions rebuilt from spot snapshots", dq.sessions_from_spot_snapshots],
            ["Sessions from historical 5-min candles", dq.sessions_from_native_rows],
            ["Spot rows outside 09:15–15:30 (ignored)", dq.spot_rows_outside_session],
            ["Bars after a data gap", dq.bars_after_data_gap],
            ["Bars evaluated", diag?.bars_evaluated],
            ["Bars with missing indicators", diag?.bars_indicators_undefined],
            ["Long signals", diag?.raw_signals?.BULLISH],
            ["Short signals", diag?.raw_signals?.BEARISH],
          ] as [string, number | undefined][]
        )
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([label, v]) => (
            <Chip key={label} size="small" variant="outlined" label={`${label}: ${v}`} />
          ))}
      </Stack>
      {bySource.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }} data-testid="spot-rows-by-source">
          Spot rows by source: {bySource.map(([k, v]) => `${k} ${v}`).join(" · ")}
        </Typography>
      )}
      {invalidReasons.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }} data-testid="invalid-bar-reasons">
          Invalid bars by reason: {invalidReasons.map(([k, v]) => `${k} ${v}`).join(" · ")}
        </Typography>
      )}
      {missing.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Missing indicators (bars): {missing.map(([k, v]) => `${k} ${v}`).join(" · ")}
        </Typography>
      )}
      {rejections.length > 0 && (
        <Table size="small" data-testid="backtest-rejections">
          <TableHead>
            <TableRow>
              <TableCell>Rejected signals</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell>What it means</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rejections.map(([reason, n]) => (
              <TableRow key={reason}>
                <TableCell>{reason}</TableCell>
                <TableCell align="right">{n}</TableCell>
                <TableCell>{REJECTION_HINTS[reason] ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

type LimitKey = Exclude<keyof RsiMomentumLimits, "notes">;

/** Field order, labels, which config section each lives in, and integer-ness. */
const LIMIT_FIELDS: { key: LimitKey; label: string; section: "sizing" | "risk"; integer?: boolean; help: string }[] = [
  { key: "allocated_capital", label: "Allocated capital (₹)", section: "sizing", help: "Capital the strategy may use" },
  { key: "risk_per_trade_pct", label: "Risk per trade (%)", section: "sizing", help: "Of allocated capital, 0–10" },
  { key: "max_lots", label: "Max lots per trade", section: "sizing", integer: true, help: "Hard cap; larger trades are rejected" },
  { key: "max_order_value", label: "Max order value (₹)", section: "sizing", help: "Premium × quantity per order" },
  { key: "max_premium_outlay", label: "Max premium outlay (₹)", section: "sizing", help: "Total premium in open positions" },
  { key: "max_daily_margin", label: "Max daily margin (₹)", section: "sizing", help: "Margin used across the day" },
  { key: "max_consecutive_losses", label: "Max consecutive losses", section: "risk", integer: true, help: "Entries halt after this streak" },
  { key: "max_daily_loss", label: "Max daily loss (₹)", section: "risk", help: "Realized loss that halts entries" },
  {
    key: "max_daily_loss_including_unrealized",
    label: "Max daily loss incl. unrealized (₹)",
    section: "risk",
    help: "Realized + open loss that halts entries",
  },
];

function initialLimitValues(effective: Record<string, unknown> | null): Record<LimitKey, string> {
  const out = {} as Record<LimitKey, string>;
  for (const f of LIMIT_FIELDS) {
    const section = (effective?.[f.section] ?? {}) as Record<string, unknown>;
    const v = section[f.key];
    out[f.key] = v === null || v === undefined ? "" : String(v);
  }
  return out;
}

/**
 * "Limits & capital" -- edits allocated capital and every mandatory hard
 * limit. Saving creates a new active config version (server validates; a
 * rejected save changes nothing). Success/errors are shown inside the card.
 */
function LimitsCard({ effective, missing, onSaved, lotInfo }: {
  effective: Record<string, unknown> | null; missing: string[]; onSaved: () => Promise<void>;
  lotInfo?: { symbol_code: string; lot_size: number; source: string } | null;
}) {
  const [values, setValues] = useState<Record<LimitKey, string>>(() => initialLimitValues(effective));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const fieldError = (f: (typeof LIMIT_FIELDS)[number]): string | null => {
    const raw = values[f.key].trim();
    if (raw === "") return "Required";
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return "Must be greater than 0";
    if (f.integer && !Number.isInteger(n)) return "Whole number";
    if (f.key === "risk_per_trade_pct" && n > 10) return "At most 10";
    return null;
  };
  const invalid = LIMIT_FIELDS.some((f) => fieldError(f) !== null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const payload = Object.fromEntries(LIMIT_FIELDS.map((f) => [f.key, Number(values[f.key])])) as unknown as RsiMomentumLimits;
      const res = await rsiMomentumService.setLimits(payload);
      setOk(`Saved as config v${res.version}.`);
      await onSaved();
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="limits-card">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Limits &amp; capital
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mandatory hard limits -- trades that would breach any of them are rejected, never scaled down. Required for paper, live and backtest.
          Saving creates a new config version; it does not enable the strategy or live trading.
        </Typography>
        {lotInfo && (
          <Typography variant="body2" sx={{ mb: 2 }} data-testid="lot-size">
            <strong>{lotInfo.symbol_code} lot size: {lotInfo.lot_size}</strong>{" "}
            <Typography component="span" variant="caption" color="text.secondary">
              — from the instrument master; used for sizing, costs and backtests. Check it against the current NSE contract specification.
            </Typography>
          </Typography>
        )}
        {missing.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Not set yet: {missing.join(", ")}
          </Alert>
        )}
        <Grid container spacing={2}>
          {LIMIT_FIELDS.map((f) => {
            const fe = values[f.key] === "" ? null : fieldError(f);
            return (
              <Grid key={f.key} item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={f.label}
                  value={values[f.key]}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  error={fe !== null}
                  helperText={fe ?? f.help}
                  inputProps={{ min: 0, step: f.integer ? 1 : "any", "aria-label": f.label }}
                />
              </Grid>
            );
          })}
        </Grid>
        {err && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="limits-error" onClose={() => setErr(null)}>
            {err}
          </Alert>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="limits-success" onClose={() => setOk(null)}>
            {ok}
          </Alert>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" disabled={invalid || saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save limits"}
          </Button>
          <Button disabled={saving} onClick={() => setValues(initialLimitValues(effective))}>
            Reset
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** "LONG missed: CLOSE_IN_UPPER_RANGE (0.42 < 0.7)" for the side that came
 * closest (at most two failed conditions); null when neither side was close. */
function nearMiss(conditions: { name: string; direction: string; passed: boolean; value?: number | null; threshold?: number; op?: string }[] | null): string | null {
  if (!conditions || conditions.length === 0) return null;
  const sides: [string, string][] = [["BULLISH", "LONG"], ["BEARISH", "SHORT"]];
  let best: { label: string; failed: string[] } | null = null;
  for (const [dir, label] of sides) {
    const failed = conditions
      .filter((c) => (c.direction === dir || c.direction === label) && !c.passed)
      .map((c) =>
        c.threshold !== undefined && c.threshold !== null
          ? `${c.name} (${c.value ?? "n/a"} ${c.op === ">=" ? "<" : ">"} ${c.threshold})`
          : c.name,
      );
    if (failed.length > 0 && failed.length <= 2 && (best === null || failed.length < best.failed.length)) {
      best = { label, failed };
    }
  }
  return best ? `${best.label} missed: ${best.failed.join(", ")}` : null;
}

export default function RsiMomentumPage() {
  const [mode, setMode] = useState<"PAPER" | "LIVE">("PAPER");
  const [data, setData] = useState<RsiMomentumDashboard | null>(null);
  const [history, setHistory] = useState<RsiMomentumPosition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [killOpen, setKillOpen] = useState(false);
  const [flatten, setFlatten] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [btFrom, setBtFrom] = useState("");
  const [btTo, setBtTo] = useState("");
  const [btModel, setBtModel] = useState<"OPTION_DELTA" | "FUTURES">("OPTION_DELTA");
  const [btBars, setBtBars] = useState<"NATIVE" | "FROM_1MIN">("NATIVE");
  const [btMaxMissing, setBtMaxMissing] = useState("3");
  const [btRef, setBtRef] = useState<"DATED" | "CURRENT">("DATED");
  const btMaxMissingOk = btBars !== "FROM_1MIN" || (btMaxMissing !== "" && Number(btMaxMissing) >= 0 && Number(btMaxMissing) <= 15);
  const [bt, setBt] = useState<RsiMomentumBacktestResult | null>(null);
  const [btBusy, setBtBusy] = useState(false);
  const [btError, setBtError] = useState<string | null>(null);
  const [btWhatIf, setBtWhatIf] = useState(false);
  const [btTab, setBtTab] = useState<"results" | "verify">("results");
  const [runs, setRuns] = useState<RsiBacktestRunSummary[] | null>(null);
  const loadRuns = async () => {
    try {
      setRuns(await rsiBacktestVerificationService.listRuns(50));
    } catch (err) {
      setBtError(extractErrorMessage(err));
    }
  };
  const openRun = async (id: string) => {
    if (!id) return;
    setBtBusy(true);
    setBtError(null);
    try {
      const r = await rsiBacktestVerificationService.getRun(id);
      setBt(r);
      // keep the backtest form in sync with the reopened run's own range
      const range = r as unknown as { from_date?: string; to_date?: string };
      if (range.from_date) setBtFrom(range.from_date);
      if (range.to_date) setBtTo(range.to_date);
      setBtWhatIf(Boolean((r as unknown as { what_if?: boolean }).what_if));
      setBtTab("results");
    } catch (err) {
      setBtError(extractErrorMessage(err));
    } finally {
      setBtBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [dash, hist] = await Promise.all([rsiMomentumService.getDashboard(mode), rsiMomentumService.getHistory(mode, 100)]);
      setData(dash);
      setHistory(hist);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [mode]);

  useEffect(() => {
    void load();
    const timer = globalThis.setInterval(() => void load(), REFRESH_MS);
    return () => globalThis.clearInterval(timer);
  }, [load]);

  const act = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runBacktest = async (rules?: RsiMomentumRules) => {
    setBtBusy(true);
    setBt(null);
    setBtError(null);
    setBtWhatIf(Boolean(rules));
    setBtTab("results");
    try {
      setBt(await rsiMomentumService.runBacktest({
        from_date: btFrom, to_date: btTo, pnl_model: btModel, ...(rules ? { rules_override: rules } : {}),
        ...(btBars === "FROM_1MIN" ? { bar_source: "FROM_1MIN" as const, max_missing_minutes_per_session: Number(btMaxMissing) } : {}),
        ...(btRef === "CURRENT" ? { reference_data: "CURRENT" as const } : {}),
      }));
    } catch (err) {
      setBtError(extractErrorMessage(err));
    } finally {
      setBtBusy(false);
    }
  };

  if (!data && !error) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const enabled = data?.algo.execution_status === "LIVE";
  const state = data ? STATE_LABEL[data.state] : null;
  const pos = data?.current_position ?? null;
  const ind = data?.indicators ?? null;
  const risk = data?.risk_usage;

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            RSI Momentum
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <span data-testid="rules-summary">{rulesSummary(data?.config.effective ?? null)}</span>
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {data && (
            <Chip
              data-testid="trading-mode"
              label={data.trading_mode_label === "LIVE" ? "LIVE TRADING" : "PAPER TRADING"}
              color={data.trading_mode_label === "LIVE" ? "error" : "info"}
            />
          )}
          {state && <Chip data-testid="strategy-state" label={state.label} color={state.color} variant="outlined" />}
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="PAPER">Paper book</ToggleButton>
            <ToggleButton value="LIVE">Live book</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {data?.evaluation && data.evaluation.state !== "EVALUATING" && data.algo.execution_status === "LIVE" && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="not-evaluating">
          <strong>Not evaluating — no paper or live trades can happen.</strong> {data.evaluation.reason}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {(data?.exit_attention ?? []).map((x) => (
        <Alert
          key={x.position_id}
          severity="error"
          sx={{ mb: 2 }}
          data-testid="exit-stalled"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={busy}
              onClick={() => void act(() => rsiMomentumService.retryExit(x.position_id), "Exit retry requested.")}
            >
              Retry exit
            </Button>
          }
        >
          <strong>EXIT STALLED — manual action may be required.</strong> {x.contract} [{x.mode}] is still OPEN:{" "}
          {x.kind === "EXIT_REJECTED"
            ? `the broker rejected the ${x.trigger ?? ""} exit${x.rejections ? ` ${x.rejections} time(s)` : ""}.`
            : `no usable option price for the ${x.trigger ?? ""} exit${x.attempts ? ` after ${x.attempts} attempts` : ""}.`}{" "}
          Retry the exit, or use Kill switch → flatten to force-close it.
          {x.detail && (
            <Typography variant="caption" sx={{ display: "block" }}>
              {x.detail}
            </Typography>
          )}
        </Alert>
      ))}

      {data && !data.config.valid && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="config-invalid">
          Active config cannot trade -- new entries are refused (open positions are still managed).
          {data.config.missing_limits.length > 0 && <> Missing mandatory limits: {data.config.missing_limits.join(", ")}.</>}
          <Typography variant="caption" sx={{ display: "block" }}>
            {data.config.errors.join(" ")}
          </Typography>
        </Alert>
      )}

      {data && (
        <>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant={enabled ? "outlined" : "contained"}
                  disabled={busy}
                  onClick={() =>
                    act(
                      () => algoExecutionService.setExecutionStatus(data.algo.id, { execution_status: enabled ? "DISABLED" : "LIVE" }),
                      enabled ? "Strategy disabled." : "Strategy enabled (paper unless every live gate is on).",
                    )
                  }
                >
                  {enabled ? "Disable strategy" : "Enable strategy"}
                </Button>
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={() =>
                    act(
                      () => algoExecutionService.setEntriesPaused(data.algo.id, { paused: !data.algo.entries_paused }),
                      data.algo.entries_paused ? "New entries resumed." : "New entries paused.",
                    )
                  }
                >
                  {data.algo.entries_paused ? "Resume new entries" : "Pause new entries"}
                </Button>
                <Button color="error" variant="contained" disabled={busy} onClick={() => setKillOpen(true)}>
                  Kill switch
                </Button>
                <Button color="warning" variant="outlined" disabled={busy} onClick={() => setLiveOpen(true)}>
                  {data.live_gates.strategy_live_approved ? "Revoke live approval" : "Approve live trading"}
                </Button>
                <Button disabled={busy} onClick={() => void load()}>
                  Refresh
                </Button>
                <Button href="/admin/strategies/rsi-momentum/trade-review" variant="outlined">
                  Trade review
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                {(
                  [
                    ["ALGO_LIVE_EXECUTION_ENABLED (server)", data.live_gates.env_algo_live_execution_enabled],
                    ["Global live switch", data.live_gates.global_live_execution_enabled],
                    ["Broker account selected", data.live_gates.live_broker_account_selected],
                    ["Strategy live approval", data.live_gates.strategy_live_approved],
                  ] as [string, boolean][]
                ).map(([label, ok]) => (
                  <Chip key={label} size="small" label={`${label}: ${ok ? "ON" : "OFF"}`} color={ok ? "warning" : "default"} variant="outlined" />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Real orders are placed only when all four gates are ON. Config v{data.config.version ?? "—"} · hash{" "}
                {data.config.hash?.slice(0, 12) ?? "—"}
              </Typography>
            </CardContent>
          </Card>

          <LimitsCard
            key={`${data.config.version ?? "none"}-${data.config.hash ?? ""}`}
            effective={data.config.effective}
            missing={data.config.missing_limits}
            onSaved={load}
            lotInfo={data.instrument}
          />

          <RsiMomentumRulesCard
            key={`rules-${data.config.version ?? "none"}-${data.config.hash ?? ""}`}
            effective={data.config.effective}
            onSaved={load}
            onWhatIf={(rules) => {
              void runBacktest(rules);
              globalThis.document?.getElementById("rsi-backtest-card")?.scrollIntoView?.({ behavior: "smooth" });
            }}
            whatIfReady={Boolean(btFrom && btTo) && !btBusy}
          />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    NIFTY context (last completed 5-min bar)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }} data-testid="trend-context">
                    {trendContext(data)}
                  </Typography>
                  <Grid container spacing={1}>
                    {(
                      [
                        ["Close", ind?.close],
                        ["RSI(9)", ind?.rsi],
                        ["EMA(3) of RSI", ind?.rsi_ema],
                        ["WMA(21) of RSI", ind?.rsi_wma],
                        ["EMA(20)", ind?.ema],
                        [vwapLabel(ind?.vwap_source), ind?.vwap],
                        ["ATR(14)", ind?.atr],
                      ] as [string, number | null | undefined][]
                    ).map(([label, v]) => (
                      <Grid key={label} item xs={6} sm={3}>
                        <Metric label={label} value={fmt(v)} />
                      </Grid>
                    ))}
                  </Grid>
                  {data.latest_signal && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                      Latest bar {time(data.latest_signal.candle_start)} · {data.latest_signal.signal_type}{" "}
                      {data.latest_signal.direction !== "NONE" ? data.latest_signal.direction : ""}
                      {data.latest_signal.suppressed_by ? ` · blocked: ${data.latest_signal.suppressed_by}` : ""}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Current position
                  </Typography>
                  {pos ? (
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Side" value={`${pos.side} (${pos.option_type})`} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Contract" value={`${fmt(pos.strike, 0)} ${pos.option_type} ${pos.expiry_date}`} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Qty (lots)" value={`${pos.quantity} (${pos.lots ?? "—"})`} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="NIFTY entry" value={fmt(pos.underlying_entry_price)} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Stop-loss (NIFTY)" value={fmt(pos.underlying_stop_loss)} tone="error" />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Target (NIFTY)" value={fmt(pos.underlying_target)} tone="success" />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Option entry" value={fmt(pos.entry_price)} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric label="Option last (chain snapshot, display only)" value={fmt(pos.current_option_price)} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Metric
                          label="Unrealized P&L"
                          value={money(pos.unrealized_pnl)}
                          tone={(pos.unrealized_pnl ?? 0) >= 0 ? "success" : "error"}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Flat -- no open position.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Today ({data.today.date}) &amp; risk usage
                  </Typography>
                  <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
                    <Metric label="Realized P&L" value={money(data.today.realized_pnl)} tone={data.today.realized_pnl >= 0 ? "success" : "error"} />
                    <Metric label="Unrealized P&L" value={money(data.today.unrealized_pnl)} />
                    <Metric label="Capital" value={money(risk?.allocated_capital)} />
                  </Stack>
                  <Typography variant="body2">
                    Trades {risk?.trades_used ?? 0} / {risk?.trades_max ?? "—"}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={risk?.trades_max ? Math.min(100, (risk.trades_used / risk.trades_max) * 100) : 0}
                    sx={{ mb: 1.5 }}
                  />
                  <Typography variant="body2">
                    Daily loss {money(risk?.daily_loss_used)} / {money(risk?.daily_loss_limit)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    color={(risk?.daily_loss_used_pct ?? 0) >= 80 ? "error" : "primary"}
                    value={Math.min(100, risk?.daily_loss_used_pct ?? 0)}
                  />
                  {data.today.halted && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      New entries halted today: {data.today.halted_reason}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Errors &amp; alerts
                  </Typography>
                  {data.alerts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {data.alerts.slice(0, 8).map((a, i) => (
                        <Alert key={`${a.time}-${i}`} severity={a.status.includes("ERROR") ? "error" : "warning"} data-testid="alert-row">
                          <strong>{time(a.time)}</strong> {a.summary}
                          {(a.count ?? 1) > 1 && (
                            <Typography component="span" variant="caption" sx={{ display: "block" }} data-testid="alert-repeat">
                              ×{a.count} — repeated since {time(a.first_time)}
                            </Typography>
                          )}
                        </Alert>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Signal history
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Bar (IST)</TableCell>
                    <TableCell>Signal</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell align="right">RSI</TableCell>
                    <TableCell align="right">EMA3 / WMA21</TableCell>
                    <TableCell align="right">Close / VWAP / EMA20</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.signals.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{time(s.candle_start)}</TableCell>
                      <TableCell>{s.signal_type === "ENTRY" ? `${s.direction} → ${s.option_type}` : "—"}</TableCell>
                      <TableCell>
                        {s.suppressed_by ? (
                          <Chip size="small" label={s.suppressed_by} color="warning" variant="outlined" />
                        ) : s.resolved_contract ? (
                          <Chip size="small" label="ORDER PLACED" color="success" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary" data-testid="no-signal">
                            no signal
                            {nearMiss(s.conditions) && (
                              <Typography component="span" variant="caption" sx={{ display: "block" }}>
                                {nearMiss(s.conditions)}
                              </Typography>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(s.indicators?.rsi)}</TableCell>
                      <TableCell align="right">
                        {fmt(s.indicators?.rsi_ema)} / {fmt(s.indicators?.rsi_wma)}
                      </TableCell>
                      <TableCell align="right">
                        {fmt(s.indicators?.close)} / {fmt(s.indicators?.vwap)} / {fmt(s.indicators?.ema)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Order lifecycle (append-only)
              </Typography>
              <OrdersTable rows={data.orders} />
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Entry / exit history ({mode.toLowerCase()})
              </Typography>
              <TradesTable rows={history} />
            </CardContent>
          </Card>
        </>
      )}

      <Card variant="outlined" sx={{ mb: 2 }} id="rsi-backtest-card">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Backtest (historical 5-minute NIFTY from stored OI-analysis data)
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField label="From" type="date" size="small" value={btFrom} onChange={(e) => setBtFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" size="small" value={btTo} onChange={(e) => setBtTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField select label="P&L model" size="small" value={btModel} onChange={(e) => setBtModel(e.target.value as "OPTION_DELTA" | "FUTURES")} sx={{ minWidth: 180 }}>
              <MenuItem value="OPTION_DELTA">Option (delta approx.)</MenuItem>
              <MenuItem value="FUTURES">Futures equivalent</MenuItem>
            </TextField>
            <TextField select label="Bars" size="small" value={btBars} sx={{ minWidth: 240 }}
              onChange={(e) => setBtBars(e.target.value as "NATIVE" | "FROM_1MIN")}>
              <MenuItem value="NATIVE">Stored 5-minute bars (default)</MenuItem>
              <MenuItem value="FROM_1MIN">Built from 1-minute candles</MenuItem>
            </TextField>
            {btBars === "FROM_1MIN" && (
              <TextField label="Max missing min / session" size="small" type="number" value={btMaxMissing}
                onChange={(e) => setBtMaxMissing(e.target.value)} inputProps={{ min: 0, max: 15 }} sx={{ width: 190 }} />
            )}
            <TextField select label="Reference data" size="small" value={btRef} sx={{ minWidth: 230 }}
              onChange={(e) => setBtRef(e.target.value as "DATED" | "CURRENT")} helperText="Lot size, option charges">
              <MenuItem value="DATED">As of each session (default)</MenuItem>
              <MenuItem value="CURRENT">Today&apos;s values</MenuItem>
            </TextField>
            <Button
              variant="contained"
              disabled={!btFrom || !btTo || btBusy || !btMaxMissingOk}
              onClick={() => void runBacktest()}
              title="Uses the active (saved) config"
              startIcon={btBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {btBusy ? "Running…" : "Run backtest"}
            </Button>
            <TextField
              select size="small" label="Stored runs" value="" sx={{ minWidth: 260 }}
              SelectProps={{ onOpen: () => void loadRuns() }}
              onChange={(e) => void openRun(e.target.value)}
              helperText="Reopen an earlier backtest"
            >
              {runs === null && <MenuItem value="" disabled>Loading…</MenuItem>}
              {runs?.length === 0 && <MenuItem value="" disabled>No stored runs yet</MenuItem>}
              {(runs ?? []).map((r) => (
                <MenuItem key={r.backtest_id} value={r.backtest_id}>
                  {r.created_at_ist ?? istText(r.created_at)} · {r.from_date}→{r.to_date} · {r.trade_count} trades{r.what_if ? " · what-if" : ""}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {data && !data.config.valid && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="backtest-config-warning">
              The backtest uses the active config, which is missing mandatory limits. Fill in “Limits &amp; capital” above first.
            </Alert>
          )}
          {btBusy && <LinearProgress sx={{ mt: 2 }} />}
          {btError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="backtest-error" onClose={() => setBtError(null)}>
              {btError}
            </Alert>
          )}
          {bt && (
            <Box sx={{ mt: 2 }} data-testid="backtest-result">
              <Chip size="small" sx={{ mb: 1 }} data-testid="backtest-source" color={btWhatIf ? "warning" : "default"}
                label={btWhatIf ? "What-if: unsaved trade rules on top of the active config" : "Active (saved) config"} />
              {bt.backtest_id && (
                <Tabs value={btTab} onChange={(_, v) => setBtTab(v)} sx={{ mb: 1, minHeight: 36 }}>
                  <Tab value="results" label="Results" sx={{ minHeight: 36 }} />
                  <Tab value="verify" label={`Trade Verification (${bt.metrics?.total_trades ?? 0})`} sx={{ minHeight: 36 }} data-testid="verify-tab" />
                </Tabs>
              )}
              {btTab === "verify" && bt.backtest_id ? (
                <BacktestTradeVerification backtestId={bt.backtest_id} />
              ) : (
              <>
              {bt.metrics ? (
                <>
                  <Grid container spacing={2}>
                    {(
                      [
                        ["Trades", String(bt.metrics.total_trades)],
                        ["Win rate", `${fmt(bt.metrics.win_rate_pct)}%`],
                        ["Profit factor", fmt(bt.metrics.profit_factor)],
                        ["Net P&L", money(bt.metrics.net_pnl)],
                        ["Gross P&L", money(bt.metrics.gross_pnl)],
                        ["Max drawdown", money(bt.metrics.max_drawdown_amount)],
                        ["Avg win", money(bt.metrics.avg_win)],
                        ["Avg loss", money(bt.metrics.avg_loss)],
                        ["Avg hold (min)", fmt(bt.metrics.avg_holding_minutes, 1)],
                        ["Trades / day", fmt(bt.metrics.avg_trades_per_day)],
                        ["Max losing streak", String(bt.metrics.max_losing_streak ?? "—")],
                        ["Exposure", `${fmt(bt.metrics.exposure_pct)}%`],
                        ...(bt.metrics.partial_bookings ? [["Partial bookings", String(bt.metrics.partial_bookings)] as [string, string]] : []),
                      ] as [string, string][]
                    ).map(([label, value]) => (
                      <Grid key={label} item xs={6} sm={4} md={2}>
                        <Metric label={label} value={value} />
                      </Grid>
                    ))}
                  </Grid>
                  {bt.partial_booking?.first_target_enabled && bt.partial_booking.reached_but_nothing_booked > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }} data-testid="partial-booking-warning">
                      First target reached {bt.partial_booking.first_target_reached}× but nothing was booked on{" "}
                      {bt.partial_booking.reached_but_nothing_booked} of them: partial booking sells whole lots only
                      ({Math.round((bt.partial_booking.book_fraction ?? 0) * 100)}% of the position, rounded down), so a 1-lot trade books nothing.{" "}
                      {bt.partial_booking.single_lot_trades} of {bt.partial_booking.trades} trades were 1 lot
                      (lots per trade: {Object.entries(bt.partial_booking.lots_distribution).map(([k, v]) => `${k}×${v}`).join(", ")}).
                      Raise risk per trade or capital, or don't rely on partial exits.
                    </Alert>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    {bt.bar_source === "FROM_1MIN" ? "Bars built from 1-minute candles. " : ""}{bt.assumptions}
                  </Typography>
                  <ReferenceDataNote reference={bt.reference_data} />
                </>
              ) : (
                <Alert severity="info">{bt.note ?? "No data in range."}</Alert>
              )}
              <BacktestDiagnostics bt={bt} />
              </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog open={killOpen} onClose={() => setKillOpen(false)}>
        <DialogTitle>Kill switch</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Immediately blocks all NEW entries. Open positions keep being managed (stop-loss, target, 15:15 square-off) unless you also flatten them now.
          </Typography>
          <FormControlLabel control={<Checkbox checked={flatten} onChange={(e) => setFlatten(e.target.checked)} />} label="Also close every open position now" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setKillOpen(false);
              void act(() => rsiMomentumService.killSwitch(flatten), flatten ? "Entries blocked and positions flattened." : "New entries blocked.");
            }}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={liveOpen} onClose={() => setLiveOpen(false)}>
        <DialogTitle>{data?.live_gates.strategy_live_approved ? "Revoke live approval" : "Approve live trading"}</DialogTitle>
        <DialogContent>
          {data?.live_gates.strategy_live_approved ? (
            <Typography variant="body2">The strategy will return to paper trading from the next candle.</Typography>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Approving creates a new config version. Real orders still require the server switch, the global live switch and a selected broker account.
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Type <strong>{LIVE_CONFIRMATION_PHRASE}</strong> to confirm.
              </Typography>
              <TextField fullWidth size="small" value={phrase} onChange={(e) => setPhrase(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLiveOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={!data?.live_gates.strategy_live_approved && phrase !== LIVE_CONFIRMATION_PHRASE}
            onClick={() => {
              const enable = !data?.live_gates.strategy_live_approved;
              setLiveOpen(false);
              setPhrase("");
              void act(() => rsiMomentumService.setLiveApproval(enable, phrase), enable ? "Live trading approved." : "Live approval revoked.");
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
